import React, { FC, useCallback } from 'react';
import { Descendant, Editor } from 'slate';
import { Editable, Slate, RenderPlaceholderProps } from 'slate-react';
import { isHotkey } from 'is-hotkey';
import { makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';

import { escapeFormat, toggleMark } from './helpers';
import { SlateValue } from './types';
import { serialize } from './serialization';
import { HOTKEYS } from './constants';
import { Element, EmojiSearch, Leaf, Toolbar } from './Components';
import { useEmojiContext, useSelectionContext } from './Contexts';

interface Props {
  editor: Editor;
  fieldName: string;
  placeholder: string;
  slateValue: SlateValue[];
  renderPlaceholder?(props: RenderPlaceholderProps): JSX.Element;
  onFocus(): void;
  onChange(descendant: Descendant[]): void;
}

const useStyles = makeStyles({
  textField: {
    minHeight: '200px !important',
  },
});

const SlateInstance: FC<Props> = props => {
  const { editor, fieldName, placeholder, renderPlaceholder, slateValue, onChange, onFocus } =
    props;
  const { setFieldValue } = useFormikContext();

  const classes = useStyles();
  const theme = useTheme();

  const renderElement = useCallback(elementProps => <Element {...elementProps} />, []);
  const renderLeaf = useCallback(leafProps => <Leaf {...leafProps} />, []);

  const { callbacks: selectionCallbacks } = useSelectionContext();
  const { callbacks: emojiCallbacks } = useEmojiContext();

  function handleBlur() {
    const serialized = slateValue.map(serialize).join('');
    setFieldValue(fieldName, serialized);
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      event.stopPropagation();
      escapeFormat(editor, event);
      return;
    }

    for (const hotkey in HOTKEYS) {
      if (isHotkey(hotkey, event as unknown as KeyboardEvent)) {
        event.preventDefault();
        const mark = HOTKEYS[hotkey];
        toggleMark(editor, mark);
      }
    }
  }

  function handleRenderPlaceholder({ attributes, children }: RenderPlaceholderProps) {
    const materialUIAttributes = {
      ...attributes,
      style: {
        ...attributes.style,
        opacity: theme.palette.action.disabledOpacity,
      },
    };

    return renderPlaceholder ? (
      renderPlaceholder({ attributes: materialUIAttributes, children })
    ) : (
      <span {...materialUIAttributes}>{children}</span>
    );
  }

  return (
    <Slate
      editor={editor}
      value={slateValue}
      onChange={value => {
        onChange(value);
        emojiCallbacks.onEditorChange(value);
      }}
    >
      <Editable
        spellCheck
        className={classes.textField}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        renderPlaceholder={handleRenderPlaceholder}
        onBlur={() => {
          selectionCallbacks.onEditorBlur();
          handleBlur();
        }}
        onFocus={onFocus}
        onKeyDown={event => {
          emojiCallbacks.onEditorKeyDown(event);
          handleKeyDown(event);
        }}
        onKeyUp={emojiCallbacks.onEditorKeyUp}
      />
      <Toolbar />
      <EmojiSearch />
    </Slate>
  );
};

export default SlateInstance;
